import { sortBy, isNil } from 'lodash-es';
import { LocalStorage } from '@hotjar/insights-common/lib/storage-factory';
import { SectionBuilder } from '@hotjar/webapp/features/Playbacks/Timeline/SectionBuilder';
import { showRecordingSecureModal } from '@hotjar/webapp/features/Playbacks/Modals/RecordingSecure';
import {
  nonReactSetCurrentTime,
  nonReactSetDuration,
  nonReactSetSectionInfoList,
  nonReactSetTabCount,
} from '@hotjar/webapp/features/Playbacks/store/realTimeStore';
import { Store } from '../../../store/player/types';
import { setRecordingAsWatched } from './setRecordingAsWatched';
import { shouldShowSecureModal } from './shouldShowSecureModal';
import { play } from './play';
import { jumpTo } from './jumpTo';
import { pause } from './pause';
import { fetchFeedbackData } from './fetchFeedbackData';
import { setupFeedbackPrompt } from './setupFeedbackPrompt';
import { handleAjaxErrors } from './handleAjaxErrors';
import { startRecording } from './startRecording';
import { EventMarker } from '@hotjar/webapp/features/Playbacks/types';
import { patchWindowSize } from '@hotjar/recordings/src/features/Playbacks/utils/patchWindowSize';

/**
 * Get and set recording data.
 */
export const fetchRecordingData = (store: Store): Promise<void> | undefined => {
  const {
    siteId,
    recordingId,
    sharingToken,
    isLightPlayer,
    isRetakerMode,
    isCypressMode,
    setTargetEndTime,
    setEvents,
    setRecording,
    setLastFetchedRecordingId,
    setJumpToCallback,
    setJumpStartEvent,
    setEndCallback,
  } = store.getState();

  if (!window.fetchRecording || !recordingId) return;

  return window
    .fetchRecording(siteId, recordingId)
    .then((recording) => patchWindowSize(recording))
    .then(function (recording) {
      setRecording(recording);
      setLastFetchedRecordingId(recording.id);

      const currentProtocol = window.location.protocol.replace(':', '');

      if (!recording.watched && !sharingToken && !isLightPlayer) {
        setRecordingAsWatched(siteId, recording.id);
      }

      // If this recording, for whatever reason, is loaded from the incorrect protocol - redirect it.
      if (currentProtocol !== recording.protocol) {
        window.location.href = window.location.href.replace(currentProtocol, recording.protocol);
      }

      if (shouldShowSecureModal()) {
        showRecordingSecureModal(recording.organization_name, () => {
          play(store);
        });
      }

      nonReactSetCurrentTime(0); // Current time in milliseconds.

      if (isLightPlayer || isCypressMode) {
        // We create a global object to control the recording from Selenium.
        console.log('setting up Selenium Controls');
        window.seleniumControls = {
          // When converting video for highlights, we expose a dedicated method to jump to a
          // specific point in time.
          jumpTo: function (targetTime: number, ...args: Array<unknown>) {
            console.log(`calling jumpTo method with targetTime ${targetTime}`);

            // Selenium adds the callback method as the last argument.
            const callback = Array.from(args).pop() as () => void | null;
            if (callback) {
              // We attach a callback to run when onPlayTick() runs to the end next.
              setJumpToCallback(() => {
                console.log('jumpTo method finished');
                callback();
              });
            }

            jumpTo(store, targetTime);
          },
          // When we convert to video we want to play for X seconds, but not longer than the end
          // of the recording. This method will let Selenium run play async until one of the two
          // are fulfilled.
          playTo: function (endTime: number) {
            console.log(`calling playTo method with targetEndTime ${endTime}`);

            // Selenium adds the callback method as the last argument.
            // eslint-disable-next-line prefer-rest-params
            const callback = Array.from(arguments).pop();

            setTargetEndTime(endTime);

            // When we run the callback we need to stop the playback to prevent further ticks.
            setEndCallback(() => {
              console.log('playTo method finished');
              callback();
              pause(store);
            });
            play(store);
          },
        };
      }

      if (isLightPlayer || isRetakerMode) {
        // We need to prevent skipping pauses, regardless of experiments
        LocalStorage.set('playbacksSkipPauses', false);
      } else if (window.datadogRum) {
        window.datadogRum.addRumGlobalContext('playback', {
          account_id: recording.account_id,
          recording_id: recording.id,
        });
      }

      const eventMarkers: Array<EventMarker> = [];
      const filteredEvents: Array<EventMarker> = [];
      const sidePanelEventMarkers: Array<EventMarker> = [];
      const sortedEventList = sortBy(recording.event_markers, 'time');

      let lastPageEventIndex: number | null = null;
      sortedEventList.forEach((event, index, events) => {
        const newEvent: EventMarker = { ...event, key: `${index}` };
        // We ignore scroll event for the new timeline
        if (newEvent.type === 'scroll') {
          return;
        }

        if (newEvent.type === 'page') {
          if (newEvent?.is_new_tab === false && lastPageEventIndex) {
            newEvent.is_visited_tab = newEvent.tab_index !== events[lastPageEventIndex].tab_index;
          }
          lastPageEventIndex = index;
        }

        if (newEvent.type !== 'page') {
          eventMarkers.push(newEvent);
        }

        if (newEvent.is_highlighted) {
          filteredEvents.push(newEvent);
        }

        if (newEvent.type !== 'error') {
          sidePanelEventMarkers.push(newEvent);
        }
      });

      setEvents(filteredEvents, eventMarkers, sidePanelEventMarkers);
      if (!isNil(recording.jumpstart_to_index)) {
        if (
          recording.event_markers &&
          recording.event_markers.length > recording.jumpstart_to_index + 1
        ) {
          setJumpStartEvent(recording.event_markers[recording.jumpstart_to_index]);
        }
      }

      const sectionBuilder = new SectionBuilder(recording.duration);
      nonReactSetSectionInfoList(
        sectionBuilder.generateSectionInfoList(recording.page_visit_recordings),
      );
      nonReactSetDuration(recording.duration);
      nonReactSetTabCount(recording.tab_count);

      startRecording(store);

      fetchFeedbackData(store).then(() => setupFeedbackPrompt(store));
    })
    .catch((error) => {
      // If the iframeController has already caught the errors we stop the additional error handling in order to prevent further redirects, page blinking. etc.
      if (window.iframeController && window.iframeController.getError()) {
        return;
      }
      handleAjaxErrors(error.response, siteId);
      throw error;
    });
};
